/**
 * PEARSON PROPRIETARY AND CONFIDENTIAL INFORMATION SUBJECT TO NDA
 * Copyright © 2026 Pearson Education, Inc. All Rights Reserved.
 *
 * NOTICE: All information contained herein is, and remains
 * the property of Pearson Education, Inc.  The intellectual and technical concepts contained
 * herein are proprietary to Pearson Education, Inc. and may be covered by U.S. and Foreign Patents,
 * patent applications, and are protected by trade secret or copyright law.
 * Dissemination of this information, reproduction of this material, and copying or distribution of this software
 * is strictly forbidden unless prior written permission is obtained
 * from Pearson Education, Inc.
 */

/**
 * Settings radio card styles
 *
 * @author Satyaki Bose
 * @file settingsRadioCardStyles.js
 */

import { styled } from '@mui/material/styles';
import mixins from '../../../../assets/styles/mixins.scss';
import { createScopedClasses, createScopedStyles } from '../../../../assets/styles/createScopedStyles';

const settingsRadioCardStyles = {
  card: {
    display: 'flex',
    flexDirection: 'column',
    gap: 8,
    width: '100%',
    padding: 24,
    borderRadius: 12,
    border: `1px solid ${mixins.color_elevation_alpha_12}`,
    backgroundColor: mixins.chalkWhiteFont,
    boxSizing: 'border-box',
    textAlign: 'left',
    font: 'inherit',
    color: 'inherit',

    '&:hover:not($cardDisabled)': {
      borderColor: mixins.amethyst
    }
  },
  selectableRegion: {
    display: 'flex',
    gap: 12,
    alignItems: 'flex-start',
    width: '100%',
    cursor: 'pointer',
    border: 'none',
    background: 'transparent',
    padding: 0,
    textAlign: 'left',
    font: 'inherit',
    color: 'inherit'
  },
  cardSelected: {
    border: `2px solid ${mixins.amethystColorV2}`,

    '& $selectableRegion:hover': {
      cursor: 'pointer'
    }
  },
  cardDisabled: {
    opacity: 0.6,

    '& $selectableRegion': {
      cursor: 'not-allowed'
    }
  },
  radio: {
    padding: 0,
    marginTop: 2,
    '&:hover': {
      backgroundColor: 'transparent'
    },
    '& input': {
      outline: 'none'
    },
    '&.pmuiCustomFocusVisible': {
      backgroundColor: 'transparent !important'
    }
  },
  radioOuter: {
    width: 18,
    height: 18,
    borderRadius: '50%',
    border: `2px solid ${mixins.amethyst}`,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    flexShrink: 0,
    boxSizing: 'border-box'
  },
  radioOuterSelected: {
    borderColor: mixins.amethystColorV2
  },
  radioInner: {
    width: 10,
    height: 10,
    borderRadius: '50%',
    backgroundColor: mixins.amethyst
  },
  main: {
    display: 'flex',
    flexDirection: 'column',
    gap: 8,
    flex: '1 1 0',
    minWidth: 0
  },
  title: {
    margin: 0,
    fontFamily: mixins.fontFamilyPlusSans,
    fontSize: mixins.fontSize16,
    fontWeight: mixins.fontWeight500,
    lineHeight: '20px',
    color: mixins.color_text_primary
  },
  description: {
    margin: 0,
    fontFamily: mixins.fontFamilyPlusSans,
    fontSize: mixins.fontSize14,
    fontWeight: mixins.fontWeight400,
    lineHeight: '22px',
    color: mixins.greyScaleColor
  },
  extraContent: {
    display: 'flex',
    alignItems: 'center',
    gap: 8,
    flexWrap: 'wrap',
    width: '100%',
    paddingLeft: 30
  },
  extraLabel: {
    fontFamily: mixins.fontFamilyPlusSans,
    fontSize: mixins.fontSize16,
    fontWeight: mixins.fontWeight500,
    lineHeight: '24px',
    color: mixins.color_text_primary
  }
};

export const classes = createScopedClasses('aet-settings-radio-card', settingsRadioCardStyles);

export const scopedStyles = createScopedStyles(settingsRadioCardStyles, classes);

export const SettingsRadioCardStylesRoot = styled('div')(scopedStyles);

export default settingsRadioCardStyles;
